<script setup lang="ts">
import { Sparkles } from "@lucide/vue";
import { useI18n } from "vue-i18n";
import { RouterView } from "vue-router";

import { useSessionStore } from "@/entities/session";
import { ToastHost } from "@/shared/ui";

import BottomNav from "./BottomNav.vue";
import SidebarNav from "./SidebarNav.vue";

/**
 * Persistent app-shell адмінки (ADR-0003 D2 — widget-шар): top bar + лівий
 * sidebar з частотним поділом (IA D2) + `<RouterView/>`.
 * Стиль — нейтральний, усе через shadcn-токени (жодних hardcoded hex).
 * Назва компанії в хедері — з session-стору (ADR-0006 D1), після login/bootstrap.
 *
 * Role-gating seam (IA D3): `canManageSalon` зараз завжди `true` (owner-режим).
 * feature-021 (RBAC) замінить на `session.role === "owner"` — і зона
 * «Налаштування салону» зникне з sidebar без інших правок.
 *
 * Mobile (< md): sidebar прихований, навігацію тримає `BottomNav` (feature-044) —
 * нижня таб-панель із 3 щоденними табами + «Ще» (bottom-Sheet із зоною налаштувань).
 */
const { t } = useI18n();
const session = useSessionStore();

const canManageSalon = true; // TODO(feature-021): session.role === "owner"
</script>

<template>
  <div
    data-testid="app-layout"
    class="flex min-h-dvh bg-background text-foreground"
  >
    <aside
      class="hidden w-60 shrink-0 flex-col bg-card md:flex"
      :aria-label="t('layout.aria.sidebar')"
    >
      <div class="flex h-14 items-center gap-2 bg-primary px-4">
        <Sparkles
          aria-hidden="true"
          class="size-4 text-primary-foreground"
        />
        <span class="text-sm font-semibold tracking-tight text-primary-foreground">{{ t("app.title") }}</span>
      </div>
      <div class="flex flex-1 flex-col border-r border-border">
        <SidebarNav :can-manage-salon="canManageSalon" />
      </div>
    </aside>

    <div class="flex min-w-0 flex-1 flex-col">
      <header
        class="sticky top-0 z-30 flex h-14 items-center gap-3 bg-primary px-4 sm:px-6"
        :aria-label="t('layout.aria.topbar')"
      >
        <span
          v-if="session.companyName"
          data-testid="company-name"
          class="text-sm font-medium text-primary-foreground"
        >{{ session.companyName }}</span>
      </header>

      <!-- pb-24 на mobile звільняє місце під fixed bottom-nav; десктоп — звичний pb-6. -->
      <main class="flex-1 px-4 pt-6 pb-24 sm:px-6 md:pb-6">
        <RouterView />
      </main>
    </div>

    <!-- Нижня таб-панель (mobile-only, md:hidden всередині); десктоп тримає sidebar. -->
    <BottomNav :can-manage-salon="canManageSalon" />

    <!-- Глобальний toast-стек (ADR-0010 D6): монтується раз; teleport у <body>. -->
    <ToastHost />
  </div>
</template>
